<template>
    <q-dialog
        ref="dialogRef"
        persistent
        @hide="onDialogHide"
        @show="focusRetry"
    >
        <q-card dark class="retry-error-dialog bg-slide text-slide">
            <q-card-section class="row items-center q-pb-none">
                <q-icon
                    :name="symOutlinedWarning"
                    color="negative"
                    size="1.5rem"
                    class="q-mr-sm"
                />
                <span class="text-h6 text-slide">{{
                    title ?? "Something went wrong"
                }}</span>
            </q-card-section>

            <q-card-section>
                <p class="text-body1 text-blue-grey-1 q-mb-none text-slide">
                    {{ message }}
                </p>
            </q-card-section>
            <q-card-section v-if="error?.stack" class="text-center">
                <qrcode-vue
                    :value="safeStack"
                    :size="400"
                    level="L"
                    render-as="svg"
                />
            </q-card-section>

            <q-card-actions
                align="right"
                @keydown.arrow-right.prevent.stop="focusRetry"
                @keydown.arrow-left.prevent.stop="focusBack"
            >
                <q-btn
                    v-if="showBack"
                    ref="backBtnRef"
                    unelevated
                    color="blue-grey-7"
                    text-color="white"
                    label="Go Back"
                    @click="onDialogCancel()"
                />
                <q-btn
                    ref="retryBtnRef"
                    unelevated
                    color="primary"
                    text-color="primarytext"
                    label="Try Again"
                    @click="onDialogOK()"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script setup lang="ts">
import { symOutlinedWarning } from "@quasar/extras/material-symbols-outlined";
import QrcodeVue from "qrcode.vue";
import { useDialogPluginComponent } from "quasar";
import { computed, onMounted, ref } from "vue";

const { message, title, error, showBack = true, registerDismiss } = defineProps<{
    message: string;
    title?: string;
    error?: Error;
    showBack?: boolean;
    registerDismiss?: (fn: () => void) => void;
}>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } =
    useDialogPluginComponent();

onMounted(() => {
    registerDismiss?.(() => {
        dialogRef.value?.hide();
    });
});

const safeStack = computed(() => {
    let stack = error?.stack ?? "";
    if (error?.cause && error.cause instanceof Error) {
      stack += `\nCaused by: ${error.cause.message}`;
      stack += `${error.cause.stack}`;
    }
    const stackLines = stack
    .split('\n');
    stackLines[0] = stackLines[0]?.replace(/:\/\/[^/]+/g, `: ${error?.message ?? ""} `) ?? "";
    // 1. Remove Vite, Vue, Quasar core, and standard dependency lines
    stack = stackLines.filter(line => {
      const isInternal = /node_modules|\.vite\/|vue\.runtime|runtime-core|quasar\.esm|@vite/i.test(line)
      return !isInternal
    })
    // 2. Shorten paths and strip Vite's cache and local file system noise
    .map(line => {
      return line
        // Removes Vite's local file system prefix (e.g., /@fs/C:/project/...)
        .replace(/\/@fs\/[a-zA-Z]:\/[^/]+/g, '')
        // Removes Vite dependency version queries (e.g., ?v=a1b2c3d4)
        .replace(/\?v=[a-zA-Z0-9]+/g, '')
        // Removes protocol, host, and port (e.g., http://localhost:5173)
        .replace(/https?:\/\/[^/]+/g, '')
        // Shortens the standard stack 'at' prefix to '@'
        .replace(/^\s*at\s+/g, '@')
        .trim()
    })
    // 3. Drop empty lines, merge, and enforce a safe maximum length
    .filter(Boolean)
    .join('\n')
    .slice(0, 2953)

    return stack;
});

const retryBtnRef = ref<{ $el: HTMLElement } | null>(null);
const backBtnRef = ref<{ $el: HTMLElement } | null>(null);

function focusRetry() {
    retryBtnRef.value?.$el.focus();
}
function focusBack() {
    backBtnRef.value?.$el.focus();
}
</script>

<style lang="scss" scoped>
.retry-error-dialog {
    width: min(90vw, 800px);
    max-width: 100%;

    :deep(.q-btn:focus) {
        box-shadow:
            0 0 0 2px var(--webtv-slide-bg),
            0 0 0 5px var(--webtv-slide-btn-focus-color);
    }
}
</style>
